import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import {
  catchError,
  filter,
  firstValueFrom,
  of,
  take,
  timeout,
} from 'rxjs';

import * as actions from './../../../core/store/user/user.actions';
import * as selectors from './../../../core/store/user/user.selectors';

// Bootstrap runs behind an APP_INITIALIZER, so anything awaited here blocks the
// whole app from rendering. It must therefore *always* resolve — if the Auth0
// SDK's startup silent-auth never settles (e.g. an expired cached token that
// can't be renewed silently: consent_required / network stall), we fail open
// and let the AuthGuard drive the login redirect instead of leaving the user
// on a blank, hung page.
const AUTH_SETTLE_TIMEOUT_MS = 8000;
const USER_LOAD_TIMEOUT_MS = 8000;

@Injectable({ providedIn: 'root' })
export class AppInitService {
  constructor(
    private auth: AuthService,
    private store: Store,
  ) {}

  async init(): Promise<void> {
    // Wait for the SDK to finish its initial checkSession, but never block
    // bootstrap indefinitely — on timeout, fail open as "not authenticated"
    // so the app boots and the AuthGuard can redirect to login.
    const isAuthenticated = await firstValueFrom(
      this.auth.isAuthenticated$.pipe(
        take(1),
        timeout(AUTH_SETTLE_TIMEOUT_MS),
        catchError(() => {
          console.warn(
            '[AppInit] Auth did not settle in time — booting unauthenticated; AuthGuard will handle redirect.',
          );
          return of(false);
        }),
      ),
    );

    if (!isAuthenticated) {
      return;
    }

    this.store.dispatch(actions.getUserMe());

    // Don't hang bootstrap if /me is slow or fails — let the app render either way.
    await firstValueFrom(
      this.store.select(selectors.getUserMe).pipe(
        filter(Boolean),
        take(1),
        timeout(USER_LOAD_TIMEOUT_MS),
        catchError(() => {
          console.warn('[AppInit] getUserMe did not resolve in time — continuing.');
          return of(null);
        }),
      ),
    );
  }
}
